import React, { useState, useEffect } from 'react';
import { FaFileInvoice, FaFilter, FaSearch, FaDownload, FaCalendarAlt, FaRupeeSign, FaDollarSign, FaChevronLeft, FaChevronRight, FaCreditCard, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../context/ToastContext';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const AdminPaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [revenueTotals, setRevenueTotals] = useState({ usd: 0, inr: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, paymentId: null, orderId: null });

  const navigate = useNavigate();
  const toast = useToast();

  const services = [
    'USA Company Formation',
    'Tax and Form Return',
    'Pay-As-You-Go',
    'Bookkeeping',
    'CFO Advisory'
  ];

  useEffect(() => {
    const fetchRevenueTotals = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/payment/revenue-totals`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
        setRevenueTotals(response.data);
      } catch (error) {
        console.error('Error fetching revenue totals:', error);
      }
    };

    fetchRevenueTotals();
  }, []);

  useEffect(() => {
    const fetchPayments = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin-login');
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/payment/all-payments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPayments(response.data);
        setFilteredPayments(response.data);
      } catch (error) {
        setError(error.response?.data?.message || error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [navigate]);

  useEffect(() => {
    filterPayments();
  }, [payments, searchQuery, statusFilter, serviceFilter, dateFilter, startDate, endDate]);

  const filterPayments = () => {
    let filtered = [...payments];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(payment =>
        payment.orderId.toLowerCase().includes(query) ||
        payment.paymentId?.toLowerCase().includes(query) ||
        payment.userDetails?.fullName?.toLowerCase().includes(query) ||
        payment.userDetails?.email?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment.status === statusFilter);
    }

    // Apply service filter
    if (serviceFilter !== 'all') {
      filtered = filtered.filter(payment => payment.service === serviceFilter);
    }

    // Apply date filter
    if (dateFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(payment =>
        new Date(payment.createdAt).toISOString().split('T')[0] === today
      );
    } else if (dateFilter === 'thisMonth') {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.createdAt);
        return (
          paymentDate.getMonth() === currentMonth &&
          paymentDate.getFullYear() === currentYear
        );
      });
    } else if (dateFilter === 'custom' && startDate && endDate) {
      filtered = filtered.filter(payment => {
        const paymentDate = new Date(payment.createdAt).getTime();
        const start = new Date(startDate).getTime();
        const end = new Date(endDate).getTime();
        return paymentDate >= start && paymentDate <= end;
      });
    }

    setFilteredPayments(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const calculateTotalRevenue = (payments, currency) => {
    return payments.reduce((sum, payment) => {
      if (payment.status !== 'completed') return sum;

      if (currency === 'INR' && payment.currency === 'INR') {
        return sum + payment.paidAmount;
      } else if (currency === 'USD' && payment.currency === 'USD') {
        return sum + payment.paidAmount;
      }
      return sum;
    }, 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    const formattedAmount = new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    }).format(amount);
    return `${symbol}${formattedAmount}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Completed</span>;
      case 'failed':
        return <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Failed</span>;
      default:
        return <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">Created</span>;
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Validate custom date range if selected
      if (dateFilter === 'custom' && (!startDate || !endDate)) {
        toast.error('Please select both start and end dates for custom range');
        return;
      }

      // Calculate dates based on filter
      let exportStartDate, exportEndDate;

      if (dateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        exportStartDate = today;
        exportEndDate = today;
      } else if (dateFilter === 'thisMonth') {
        const now = new Date();
        exportStartDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        exportEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      } else if (dateFilter === 'custom') {
        exportStartDate = startDate;
        exportEndDate = endDate;
      }

      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/payment/export-payments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          service: serviceFilter !== 'all' ? serviceFilter : undefined,
          startDate: exportStartDate,
          endDate: exportEndDate
        },
        responseType: 'blob'
      });

      // Create filename with date range if applicable
      let filename = 'payments_export';
      if (exportStartDate && exportEndDate) {
        filename += `_${exportStartDate}_to_${exportEndDate}`;
      }
      filename += '.csv';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/payment/delete-payment/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Remove from local state
      setPayments(prev => prev.filter(p => p._id !== paymentId));
      setFilteredPayments(prev => prev.filter(p => p._id !== paymentId));

      // Close confirmation dialog
      setDeleteConfirm({ show: false, paymentId: null, orderId: null });

      // Show success message
      toast.success('Payment deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete payment');
    }
  };

  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

  if (loading) {
    return <SkeletonLoader variant="page" />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg">
          <FaCreditCard className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Management</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{payments.length} total payments</p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"><FaSearch className="w-3.5 h-3.5 text-primary-500" /> Search</label>
            <input type="text" placeholder="Order ID, Payment ID, or name" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50" />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"><FaFilter className="w-3.5 h-3.5 text-primary-500" /> Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 cursor-pointer">
              <option value="all">All Statuses</option>
              <option value="created">Created</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"><FaFilter className="w-3.5 h-3.5 text-primary-500" /> Service</label>
            <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 cursor-pointer">
              <option value="all">All Services</option>
              {services.map(service => (<option key={service} value={service}>{service}</option>))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"><FaCalendarAlt className="w-3.5 h-3.5 text-primary-500" /> Date Range</label>
            <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 cursor-pointer">
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="thisMonth">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
            {dateFilter === 'custom' && (<div className="flex gap-2 mt-2"><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="flex-1 px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm" /><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="flex-1 px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white text-sm" /></div>)}
          </div>
          <div className="flex items-end">
            <button onClick={handleExportCSV} disabled={exporting || filteredPayments.length === 0} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed">{exporting ? 'Exporting...' : <><FaDownload className="w-4 h-4" /> Export CSV</>}</button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 shadow-card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Payments</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{payments.length}</p>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 shadow-card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Completed</p>
          <p className="text-3xl font-bold text-green-600 mt-1">{payments.filter(p => p.status === 'completed').length}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 shadow-card">
          <p className="text-sm text-white/80">Revenue (USD)</p>
          <p className="text-2xl font-bold text-white mt-1 flex items-center"><FaDollarSign className="w-5 h-5" />{formatCurrency(calculateTotalRevenue(payments, 'USD'), 'USD').substring(1)}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-5 shadow-card">
          <p className="text-sm text-white/80">Revenue (INR)</p>
          <p className="text-2xl font-bold text-white mt-1 flex items-center"><FaRupeeSign className="w-5 h-5" />{formatCurrency(calculateTotalRevenue(payments, 'INR'), 'INR').substring(1)}</p>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className="px-4 py-4 text-left text-sm font-semibold w-48">Order & Payment IDs</th>
                <th className="px-4 py-4 text-left text-sm font-semibold min-w-48">Customer</th>
                <th className="px-4 py-4 text-left text-sm font-semibold w-40">Service</th>
                <th className="px-4 py-4 text-left text-sm font-semibold w-28">Amount</th>
                <th className="px-4 py-4 text-left text-sm font-semibold w-36">Date</th>
                <th className="px-4 py-4 text-left text-sm font-semibold w-24">Status</th>
                <th className="px-4 py-4 text-left text-sm font-semibold w-28">Location</th>
                <th className="px-4 py-4 text-left text-sm font-semibold w-20">Invoice</th>
                <th className="px-4 py-4 text-left text-sm font-semibold w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {currentPayments.length > 0 ? (
                currentPayments.map(payment => (
                  <tr key={payment._id} className="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-mono text-gray-600 dark:text-gray-300">{payment.orderId}</p>
                      {payment.paymentId && <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{payment.paymentId}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 dark:text-white text-sm">{payment.userDetails?.fullName || 'N/A'}</p>
                      {payment.userDetails?.email && <p className="text-xs text-gray-500 dark:text-gray-400">{payment.userDetails.email}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{payment.service}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(payment.paidAmount, payment.currency)}</p>
                      {payment.gstApplied && <span className="text-xs text-gray-500">incl. GST</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDate(payment.createdAt)}</td>
                    <td className="px-4 py-3">{getStatusBadge(payment.status)}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-600 dark:text-gray-300">{payment.location === 'india' ? 'India' : 'International'}</p>
                      {payment.state && <p className="text-xs text-gray-500">{payment.state}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {payment.invoicePath ? (
                        <button onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL}/api/invoice/download/${payment.invoicePath}`, '_blank')} className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700 text-sm font-medium">
                          <FaFileInvoice className="w-4 h-4" /> View
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setDeleteConfirm({ show: true, paymentId: payment._id, orderId: payment.orderId })}
                        className="flex items-center gap-1.5 text-red-600 hover:text-red-700 text-sm font-medium"
                        title="Delete payment"
                      >
                        <FaTrash className="w-4 h-4" /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">No payments found matching your criteria</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {filteredPayments.length > paymentsPerPage && (
          <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-100 dark:border-dark-700">
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className={`p-2 rounded-lg ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}><FaChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm text-gray-600 dark:text-gray-400">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className={`p-2 rounded-lg ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}><FaChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Showing {indexOfFirstPayment + 1} to {Math.min(indexOfLastPayment, filteredPayments.length)} of {filteredPayments.length} payments</p>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <FaTrash className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Payment</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Order ID: {deleteConfirm.orderId}</p>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete this payment? This action cannot be undone and will remove all associated data.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm({ show: false, paymentId: null, orderId: null })}
                className="px-4 py-2.5 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePayment(deleteConfirm.paymentId)}
                className="px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
              >
                <FaTrash className="w-4 h-4" />
                Delete Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentManagement;